import React from 'react';
import { Box, Popover } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import { ArtifactSummary } from '../ai-chat/aiChatHelpers';
import pluralize from 'pluralize';

interface ArtifactSelectorProps {
  artifacts: ArtifactSummary[];
  currentArtifactId: string | null;
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelectArtifact: (artifactId: string) => void;
}

export const ArtifactSelector: React.FC<ArtifactSelectorProps> = ({
  artifacts,
  currentArtifactId,
  isOpen,
  anchorEl,
  onClose,
  onSelectArtifact,
}) => {
  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            marginTop: '8px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
            zIndex: 700,
          },
        },
      }}
      sx={{
        zIndex: 700,
      }}
      // Disable global backdrop - we'll use a custom one scoped to artifact panel
      BackdropProps={{
        sx: {
          backgroundColor: 'transparent',
        },
      }}
    >
      {/* Selector Panel */}
      <Box
        sx={{
          width: '360px',
          maxHeight: '400px',
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Artifact List */}
        <Box
          sx={{
            overflowY: 'auto',
            padding: '12px',
          }}
        >
          {artifacts.map((artifact) => {
            const isSelected = artifact.artifactId === currentArtifactId;

            return (
              <Box
                key={artifact.artifactId}
                onClick={() => onSelectArtifact(artifact.artifactId)}
                sx={{
                  padding: '12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? colors.grey[100] : 'transparent',
                  marginBottom: '4px',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: colors.grey[100],
                  },
                }}
              >
                <Box
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                    marginBottom: '4px',
                  }}
                >
                  <Typography variant="body" color={colors.grey[900]}>
                    {artifact.title}
                  </Typography>
                </Box>
                {/* TODO: Temporarily hidden - re-enable when versions are ready */}
                {false && (
                  <Typography variant="caption" color={colors.grey[600]}>
                    {artifact.versionCount} {pluralize('version', artifact.versionCount)} |
                    Interactive artifact
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Popover>
  );
};
